import styled from 'styled-components';
import { motion } from 'framer-motion';
import { AppleLogo, GooglePlayLogo } from '@phosphor-icons/react';
import { Container } from '@/components/common/Container';
import { Section } from '@/components/common/Section';
import { Badge } from '@/components/common/Badge';
import { theme } from '@/styles/theme';
import { fadeIn } from '@/styles/animations';
import { APP_STORE_URL, GOOGLE_PLAY_URL } from '@/utils/constants';
//@ts-ignore
import heroImage from '@/assets/hero_image.png';

const HeroWrapper = styled(Section)`
  background: ${theme.colors.white};
  min-height: calc(100vh - 80px);
  display: flex;
  align-items: center;
`;

const HeroContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.xxxl};
  align-items: center;

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.xxl};
    text-align: center;
  }
`;

const HeroText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.tablet}) {
    align-items: center;
  }
`;

const Headline = styled(motion.h1)`
  font-size: ${theme.fontSizes['6xl']};
  font-weight: ${theme.fontWeights.black};
  line-height: 1.1;
  color: ${theme.colors.text.black};

  @media (max-width: ${theme.breakpoints.desktop}) {
    font-size: ${theme.fontSizes['5xl']};
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.fontSizes['4xl']};
  }
`;

const Subheadline = styled(motion.p)`
  font-size: ${theme.fontSizes.xl};
  color: ${theme.colors.text.darkGray};
  line-height: 1.6;
  margin-bottom: ${theme.spacing.md};

  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.fontSizes.lg};
  }
`;

const HeroVisual = styled(motion.div)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: ${theme.breakpoints.tablet}) {
    order: -1;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  max-width: 550px;
  height: auto;

  @media (max-width: ${theme.breakpoints.tablet}) {
    max-width: 350px;
  }
`;

const DownloadButtons = styled(motion.div)`
  display: flex;
  gap: ${theme.spacing.md};
  flex-wrap: wrap;

  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    width: 100%;
  }
`;

const DownloadButton = styled.a<{ $variant: 'primary' | 'secondary' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: ${theme.fontWeights.semibold};
  border-radius: ${theme.borderRadius.full};
  text-decoration: none;
  transition: all ${theme.transitions.fast};

  ${({ $variant }) =>
    $variant === 'primary'
      ? `
    background: ${theme.colors.primary.main};
    color: ${theme.colors.white};

    &:hover {
      color: ${theme.colors.white};
      transform: scale(1.05);
    }
  `
      : `
    background: ${theme.colors.neutral[800]};
    color: ${theme.colors.white};

    &:hover {
      color: ${theme.colors.white};
      transform: scale(1.05);
    }
  `}

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 100%;
    justify-content: center;
  }
`;

interface HeroSectionProps {
  id?: string;
}

export const HeroSection = ({ id }: HeroSectionProps) => {
  return (
    <HeroWrapper id={id}>
      <Container>
        <HeroContent>
          <HeroText>
            <Badge $variant="success" style={{ width: 'fit-content' }}>Free to use</Badge>
            <Headline
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Expand your vocabulary, one word at a time
            </Headline>
            <Subheadline
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Make vocabulary learning feel effortless and joyful. Swipe, learn, grow.
            </Subheadline>
            <DownloadButtons
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <DownloadButton href={APP_STORE_URL} $variant="primary">
                <AppleLogo size={22} weight="fill" />
                App Store
              </DownloadButton>
              <DownloadButton href={GOOGLE_PLAY_URL} $variant="secondary">
                <GooglePlayLogo size={22} weight="fill" />
                Google Play
              </DownloadButton>
            </DownloadButtons>
          </HeroText>

          <HeroVisual variants={fadeIn} initial="hidden" animate="visible">
            <HeroImage
              src={heroImage}
              alt="Lexifox app preview"
            />
          </HeroVisual>
        </HeroContent>
      </Container>
    </HeroWrapper>
  );
};
